// Static datasets and copy for the three phases. No network, no randomness —
// every figure here is drawn onto the canvas or read by a text panel.

export type Candle = { o: number; h: number; l: number; c: number; v: number }
export type DepthRow = { price: string; size: number }
export type OptionRow = {
    strike: number
    callBid: number
    callAsk: number
    callIv: number
    putBid: number
    putAsk: number
    putIv: number
    highlight?: boolean
}

export type Review = { quote: string; author: string; role: string }
export type Seat = 'left' | 'right'

export type TextPanel = {
    phase: number
    seat: Seat
    eyebrow: string
    title: string
    body: string
    verified: string
    reviews: Review[]
    cta: string
    meta: string
}

// Phase 1 — a bullish BTC breakout from ~106K to a 107.4K close with one pullback.
export const CANDLES: Candle[] = [
    { o: 106020, h: 106180, l: 105920, c: 106140, v: 0.42 },
    { o: 106140, h: 106320, l: 106080, c: 106280, v: 0.55 },
    { o: 106280, h: 106400, l: 106210, c: 106260, v: 0.38 },
    { o: 106260, h: 106520, l: 106230, c: 106480, v: 0.61 },
    { o: 106480, h: 106690, l: 106440, c: 106650, v: 0.72 },
    { o: 106650, h: 106720, l: 106500, c: 106560, v: 0.49 },
    { o: 106560, h: 106600, l: 106320, c: 106380, v: 0.83 },
    { o: 106380, h: 106540, l: 106350, c: 106510, v: 0.44 },
    { o: 106510, h: 106780, l: 106480, c: 106740, v: 0.67 },
    { o: 106740, h: 106960, l: 106700, c: 106920, v: 0.78 },
    { o: 106920, h: 107080, l: 106870, c: 107040, v: 0.69 },
    { o: 107040, h: 107120, l: 106960, c: 106990, v: 0.51 },
    { o: 106990, h: 107260, l: 106960, c: 107220, v: 0.88 },
    { o: 107220, h: 107380, l: 107180, c: 107350, v: 0.74 },
    { o: 107350, h: 107420, l: 107280, c: 107300, v: 0.46 },
    { o: 107300, h: 107480, l: 107270, c: 107440, v: 0.81 },
    { o: 107440, h: 107460, l: 107360, c: 107432, v: 0.58 }
]

export const ASKS: DepthRow[] = [
    { price: '107,448', size: 0.184 },
    { price: '107,446', size: 0.092 },
    { price: '107,440', size: 0.451 },
    { price: '107,436', size: 0.218 }
]

export const BIDS: DepthRow[] = [
    { price: '107,432', size: 0.238 },
    { price: '107,430', size: 0.117 },
    { price: '107,428', size: 0.892 },
    { price: '107,424', size: 0.314 }
]

// Phase 2 — a XAU options chain, 26 DEC expiry, ATM at the 2680 strike.
export const OPTION_CHAIN: OptionRow[] = [
    { strike: 2640, callBid: 56.4, callAsk: 56.8, callIv: 14.2, putBid: 3.05, putAsk: 3.15, putIv: 13.8 },
    { strike: 2650, callBid: 48.2, callAsk: 48.5, callIv: 13.9, putBid: 5.2, putAsk: 5.35, putIv: 13.5 },
    { strike: 2660, callBid: 40.4, callAsk: 40.65, callIv: 13.6, putBid: 8.1, putAsk: 8.25, putIv: 13.4 },
    { strike: 2670, callBid: 32.9, callAsk: 33.15, callIv: 13.3, putBid: 12.3, putAsk: 12.5, putIv: 13.6 },
    { strike: 2680, callBid: 25.85, callAsk: 26.05, callIv: 13.4, putBid: 16.95, putAsk: 17.2, putIv: 13.9, highlight: true },
    { strike: 2690, callBid: 19.4, callAsk: 19.6, callIv: 13.7, putBid: 22.5, putAsk: 22.75, putIv: 14.3 },
    { strike: 2700, callBid: 14.05, callAsk: 14.2, callIv: 14.2, putBid: 29.1, putAsk: 29.35, putIv: 14.8 },
    { strike: 2710, callBid: 9.6, callAsk: 9.75, callIv: 14.8, putBid: 36.95, putAsk: 37.2, putIv: 15.4 }
]

export const GREEKS = { delta: 0.52, gamma: 0.018, theta: -0.45, vega: 1.2, rho: 0.34 }

// Phase 3 — a bearish equity curve indexed to 100, peak ~131, trough ~83.
export const BEAR_CURVE: number[] = [
    100, 104, 108, 113, 119, 124, 122, 127, 131, 128, 122, 116,
    118, 121, 119, 113, 106, 99, 95, 91, 88, 86, 83, 87,
    92, 95, 93, 96, 99, 102
]

export const RISK = {
    sharpe: '2.14',
    maxDrawdown: '-17.8%',
    vol: '14.2%',
    cagr: '23.8%'
}

export const TEXT_BTC: TextPanel = {
    phase: 1,
    seat: 'right',
    eyebrow: '01 · spot · live',
    title: 'The book never lies.',
    body: "Bids stack on one side, asks on the other, and the spread between them is the only honest sentence in the market. Every tick is a transfer of risk between two people who couldn't disagree more — one believes the next print is up, one is busy proving them wrong. The chart is a record of who paid.",
    verified: 'verified · n = 2 of 318',
    reviews: [
        {
            quote: "Five years on the desk and the book is still the only thing that hasn't lied to me. Open it, watch it, trade it, in that order.",
            author: 'Marcus Chen',
            role: 'Head of HFT, Helix Capital · 2025'
        },
        {
            quote: "Best primer on order flow I've handed to a new hire in a decade. Got both juniors up to speed before lunch.",
            author: 'Yelena Krasniqi',
            role: 'Quant strategist, Veriton · 2025'
        }
    ],
    cta: 'Open the live book',
    meta: 'btc·usd · binance.us · 1h · last sync 12 ms'
}

export const TEXT_GOLD: TextPanel = {
    phase: 2,
    seat: 'left',
    eyebrow: '02 · options · 26 dec',
    title: 'Buy time. Sell variance.',
    body: 'An option is a contract on uncertainty, priced in five letters. Delta is how much the contract walks with the metal. Theta is the rent you pay every day you hold it open. Vega is the bet on volatility you placed whether you meant to or not. The chain teaches you to think in shape, not in price.',
    verified: 'verified · n = 2 of 204',
    reviews: [
        {
            quote: "If you can read this chain, you can hedge anything. If you can't, you'll learn quickly — and that's exactly the point.",
            author: 'Tomáš Reinhardt',
            role: 'Commodities prop, Lattice Markets · 2025'
        },
        {
            quote: "The greeks aren't math, they're a way of seeing. This page gets that exactly right.",
            author: 'Adetokunbo Adeyemi',
            role: 'Vol trader, Meridian Quant · 2024'
        }
    ],
    cta: 'Pull up the chain',
    meta: 'xau·usd · cboe · spot 2,684.50 · iv30 13.8 %'
}

export const TEXT_PORT: TextPanel = {
    phase: 3,
    seat: 'right',
    eyebrow: '03 · net liquidation',
    title: "Risk is what's left over.",
    body: "Position sizing isn't a number; it's a worldview. Sharpe tells you whether the market paid you for the risk you carried. Drawdown is the part of the year you'd rather not remember. Net liquidation is the only figure that is ever true at any given moment — everything else is a draft.",
    verified: 'verified · n = 2 of 156',
    reviews: [
        {
            quote: "Took me a decade to learn what this page says in two paragraphs. Wish I'd had it in '18.",
            author: 'Hana Müller',
            role: 'Portfolio manager, Northstar Partners · 2025'
        },
        {
            quote: 'Net liquidation is the only metric I check before bed. Everything else is theatre.',
            author: 'Diego Ferrera',
            role: 'Systematic fund, Aresel Capital · 2024'
        }
    ],
    cta: 'Run my numbers',
    meta: "marked to market · every second · since '21"
}

export const PANELS: TextPanel[] = [TEXT_BTC, TEXT_GOLD, TEXT_PORT]
